<template>
  <CustomDropdown
    ref="moreOptionsDropdownRef"
    gap="8px"
    position="end"
    dropdownCSS="p-0"
    overflowY="visible"
  >
    <template #trigger>
      <div
        class="flex items-center justify-center p-2 rounded-lg bg-[--white-opacity-2] border_8 cursor-pointer"
        :class="{
          'bg-[--white-opacity-4] border_12': isMoreOptionsOpen,
        }"
      >
        <ThemeImage
          :icon="imageNames.MORE_VERTICAL"
          alt="more_options"
          class="size-4"
        />
      </div>
    </template>

    <template #default>
      <div
        v-if="
          !showManageColumnsContent &&
          !showPermissionsContent &&
          !showLayoutContent &&
          !showGroupByContent
        "
        :class="['flex flex-col', exportOnly ? 'w-fit' : 'w-[266px]']"
      >
        <div v-if="!exportOnly" class="flex flex-col py-3 px-2">
          <a-input
            id="view_name_input"
            v-model:value="localViewName"
            class="input_medium"
            :placeholder="
              currentMode === 'create' ? 'View Name' : 'Rename view'
            "
            :disabled="!isEditable"
            allow-clear
          />
        </div>

        <div v-if="!exportOnly" class="flex flex-col px-3 pb-3 gap-2 proxima_nova">
          <template v-for="option in navigationOptions" :key="option.key">
            <div
              v-if="option.visible"
              class="flex items-center !justify-between px-3 py-2 rounded-lg"
              :class="
                option.disabled
                  ? 'cursor-not-allowed hover:bg-transparent text_color'
                  : 'dropdown_item cursor-pointer'
              "
              @click="!option.disabled && option.onClick()"
            >
              <span class="flex items-center gap-2">
                <ThemeImage
                  v-if="option.icon"
                  :icon="option.icon"
                  class="size-3"
                  :alt="option.key + '_icon'"
                />
                <span class="text-sm">{{ option.label }}</span>
              </span>

              <span class="flex items-center gap-2 min-w-0 flex-shrink">
                <span
                  v-if="option.value"
                  class="text-sm secondary_color truncate max-w-[120px]"
                  :title="option.value"
                  >{{ option.value }}</span
                >
                <ThemeImage
                  :icon="imageNames.RIGHT_ARROW_SMALL_GREY"
                  class="size-4 flex-shrink-0"
                  alt="go_right"
                />
              </span>
            </div>
          </template>
        </div>

        <div
          v-if="currentMode === 'edit' || exportOnly"
          :class="['flex flex-col gap-2', exportOnly ? 'p-2' : 'pb-2 px-2']"
        >
          <div
            v-if="showExportOption"
            class="flex items-center gap-1.5 px-3 py-2 rounded-lg flex-shrink-0"
            @click="handleOpenExportModal"
            :class="[
              hasNoResults
                ? 'cursor-not-allowed hover:bg-transparent'
                : 'dropdown_item cursor-pointer',
            ]"
          >
            <ThemeImage
              :icon="imageNames.EXPORT_GREY"
              alt="export_csv"
              class="size-4"
            />
            <span
              class="text-sm"
              :class="{
                secondary_color: hasNoResults,
              }"
              >Export CSV</span
            >
          </div>
        </div>

        <div v-if="!exportOnly" class="flex flex-col px-3 pb-3">
          <a-button
            class="primary_medium"
            @click="handleSaveView"
            :disabled="!localViewName || !localViewName.trim() || isSavingView || !isEditable"
            :loading="isSavingView"
          >
            Done
          </a-button>
        </div>
      </div>

      <!-- Layout Content -->
      <div v-else-if="showLayoutContent" class="flex flex-col w-[266px] p-3">
        <ViewTypePanel
          :view-type="localViewType"
          :show-back-button="true"
          @update:viewType="handleLayoutChange"
          @go-back="goBackToOptions"
        />
      </div>

      <!-- Group By Content -->
      <div v-else-if="showGroupByContent" class="flex flex-col w-[266px] p-3">
        <ViewGroupByPanel
          ref="groupByPanelRef"
          :group-by="localGroupBy"
          :entity-slug="entitySlug"
          :service="service"
          :project-id="projectId"
          :current-view="currentMode === 'create' ? null : currentView"
          :show-back-button="true"
          @update:groupBy="handleGroupByChange"
          @go-back="goBackToOptions"
          @done="handleSaveView"
        />
      </div>

      <!-- Permissions Content -->
      <div
        v-else-if="showPermissionsContent"
        class="flex flex-col w-[266px] p-3"
      >
        <ViewPermissionsPanel
          :access-level="localAccessLevel"
          :selected-members="selectedMembersLocal"
          :access-options-by-tab="accessOptionsByTab"
          :current-user-as-non-removable="currentUserAsNonRemovable"
          :enable-auto-select-current-user="shouldEnableAutoSelect"
          :selection-by-tab="selectionByTab"
          :show-back-button="true"
          dropdown-position="end"
          @update:accessLevel="(val) => (localAccessLevel = val)"
          @update:selectedMembers="(val) => (selectedMembersLocal = val)"
          @selection-change="handleSelectionChange"
          @go-back="goBackToOptions"
        />
        <div class="border_top pb-1">
          <a-button class="primary_medium w-full" @click="handleSaveView">Done</a-button>
        </div>
      </div>

      <!-- Manage Columns Content -->
      <div v-else-if="showManageColumnsContent" class="flex flex-col">
        <ManageColumnsContent
          :slug="entitySlug"
          :currentColumns="currentColumns"
          :frozenColumns="frozenColumns"
          :viewId="viewId"
          :viewType="localViewType"
          :projectId="projectId"
          :service="props.service"
          @columns-updated="handleColumnsUpdated"
          @frozen-columns-updated="handleFrozenColumnsUpdated"
          @go-back="goBackToOptions"
        />
      </div>
    </template>
  </CustomDropdown>
</template>

<script setup>
import {
  ref,
  watch,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useStore } from "vuex";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ManageColumnsContent from "@/src/components/custom_object_table_components/ManageColumnsContent.vue";
import ViewTypePanel from "@/src/components/view_components/ViewTypePanel.vue";
import ViewGroupByPanel from "@/src/components/view_components/ViewGroupByPanel.vue";
import ViewPermissionsPanel from "@/src/components/view_components/ViewPermissionsPanel.vue";
import eventBus from "@/src/utils/eventBus.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { accessLevels, sharingScopes } from "@/src/constants/viewConstants";
import {
  entityTypes,
  viewEntityMapping,
} from "@/src/constants/entityTypeConstants.js";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import ViewsServiceV2 from "@/src/api/ViewsServiceV2.js";
import { loadGroupByOptions } from "@/src/utils/viewHelpers.js";

const props = defineProps({
  slug: { type: String, default: "" },
  hasNoResults: { type: Boolean, default: false },
  showExportOption: { type: Boolean, default: false },
  currentColumns: { type: Array, default: () => [] },
  frozenColumns: { type: Array, default: () => [] },
  viewId: { type: [String, Number], default: null },
  accessOptionsByTab: {
    type: Object,
    default: () => ({ Users: [], Teams: [] }),
  },
  entitySlug: { type: String, default: "" },
  currentView: { type: Object, default: null },
  sharingDetails: { type: Object, default: null },
  projectId: { type: [String, Number], default: null },
  service: { type: String, default: null },
  scopeEntityId: { type: [String, Number], default: null },
  hidePermissionsOption: { type: Boolean, default: false },
  exportOnly: { type: Boolean, default: false },
});

const emit = defineEmits([
  "columns-updated",
  "frozen-columns-updated",
  "open-export-modal",
  "view-created",
  "view-updated",
]);

const store = useStore();
const moreOptionsDropdownRef = ref(null);
const groupByPanelRef = ref(null);
const isMoreOptionsOpen = ref(false);
const showManageColumnsContent = ref(false);
const showPermissionsContent = ref(false);
const showLayoutContent = ref(false);
const showGroupByContent = ref(false);
const currentMode = ref(null);
const isSavingView = ref(false);

const localViewName = ref("");
const localViewType = ref("table");
const localGroupBy = ref(null);
const localAccessLevel = ref(accessLevels.EVERYONE);
const selectedMembersLocal = ref([]);
const selectionByTab = ref({ users: [], teams: [] });
const dropdowns = ref({
  viewType: false,
  groupBy: false,
  permissions: false,
});

const groupByConfigData = ref({
  selectedPropertyMeta: null,
  groupOptions: [],
});

const initialState = ref({
  name: "",
  viewType: "table",
  groupBy: null,
  accessLevel: accessLevels.EVERYONE,
  sharingScope: sharingScopes.PERSONAL,
  teamIds: [],
  userIds: [],
});

const openManageColumns = () => {
  showManageColumnsContent.value = true;
};

const openLayout = () => {
  showLayoutContent.value = true;
};

const openGroupBy = () => {
  if (
    groupByConfigData.value?.selectedPropertyMeta?.isPipelineGroupBy &&
    groupByConfigData.value?.selectedPropertyMeta?.id
  ) {
    const uniqueId = groupByConfigData.value.selectedPropertyMeta.id;

    localGroupBy.value = uniqueId;
  }

  showGroupByContent.value = true;
};

const openPermissions = () => {
  if (currentMode.value === "edit" && props.currentView) {
    const userIds = props.currentView.user_ids || [];
    const teamIds = props.currentView.team_ids || [];

    const hasNoSelections =
      selectionByTab.value.users.length === 0 &&
      selectionByTab.value.teams.length === 0;

    if (hasNoSelections) {
      selectionByTab.value = {
        users: [...userIds],
        teams: [...teamIds],
      };
      selectedMembersLocal.value = [...userIds, ...teamIds];
    }
  }
  showPermissionsContent.value = true;
};

const goBackToOptions = () => {
  if (showGroupByContent.value && groupByPanelRef.value) {
    groupByConfigData.value = {
      selectedPropertyMeta: groupByPanelRef.value.selectedPropertyMeta,
      groupOptions: groupByPanelRef.value.groupOptions || [],
    };
  }

  showManageColumnsContent.value = false;
  showPermissionsContent.value = false;
  showLayoutContent.value = false;
  showGroupByContent.value = false;
};

const handleOpenExportModal = () => {
  if (props.hasNoResults) {
    return;
  }
  emit("open-export-modal");
};

const handleColumnsUpdated = (updatedColumns) => {
  emit("columns-updated", updatedColumns);
};

const handleFrozenColumnsUpdated = (updatedFrozenColumns) => {
  emit("frozen-columns-updated", updatedFrozenColumns);
};

const closeDropdown = (key) => {
  dropdowns.value[key] = false;
};

const loadGroupOptionsForAutoSelect = async (field) => {
  try {
    const formattedOptions = await loadGroupByOptions(field, props.entitySlug, {
      currentView: null,
      projectId: props.projectId,
      preserveSavedConfig: false,
    });

    groupByConfigData.value = {
      selectedPropertyMeta: field,
      groupOptions: formattedOptions,
    };
  } catch (error) {
    console.error("Error loading group options for auto-select:", error);
    groupByConfigData.value = {
      selectedPropertyMeta: field,
      groupOptions: [],
    };
  }
};

const handleViewTypeChange = (type) => {
  localViewType.value = type;
  if (type === "table") {
    localGroupBy.value = null;
  }
};

const handleLayoutChange = async (type) => {
  localViewType.value = type;
  if (type === "table") {
    localGroupBy.value = null;
    groupByConfigData.value = {
      selectedPropertyMeta: null,
      groupOptions: [],
    };
  } else if (type === "board") {
    if (!localGroupBy.value) {
      const isTasks = props.entitySlug === entityTypes.TASKS;
      const isDeal = props.entitySlug === entityTypes.DEALS;

      if (isTasks && props.projectId) {
        const schemaResult = await store.dispatch("schema/fetchSchema", {
          slug: props.entitySlug,
          projectId: props.projectId,
        });
      }

      if (isDeal) {
        const isPipelinesLoaded = store.getters["pipelines/isPipelinesLoaded"];
        if (!isPipelinesLoaded) {
          await store.dispatch("pipelines/fetchPipelines");
        }
      }

      const selectFields =
        store.getters["schema/getSelectFieldsBySlug"](
          props.entitySlug,
          isTasks ? props.projectId : null,
          { excludeGroupBy: true },
          props.service,
        ) || [];

      let fieldsToCheck = selectFields;
      if (isDeal) {
        const pipelines = store.getters["pipelines/allPipelines"] || [];
        const pipelineFields = pipelines.map((pipeline) => ({
          id: `pipeline_${pipeline.id}`,
          slug: "pipeline_id",
          name: pipeline.name,
          type: "pipeline",
          isPipelineGroupBy: true,
          pipelineId: pipeline.id,
        }));
        fieldsToCheck = [...pipelineFields, ...selectFields];
      }

      if (fieldsToCheck.length > 0) {
        const firstSelectField = fieldsToCheck[0];
        localGroupBy.value = firstSelectField.slug;

        try {
          await loadGroupOptionsForAutoSelect(firstSelectField);
        } catch (error) {
          console.error(
            "❌ [ViewMoreOptions handleLayoutChange] Error loading group options:",
            error,
          );
        }
      }
    }
  }
  goBackToOptions();
};

const handleGroupByChange = (value) => {
  const currentSlug = groupByConfigData.value?.selectedPropertyMeta?.slug;

  if (currentSlug !== value) {
    localGroupBy.value = value;
    groupByConfigData.value = {
      selectedPropertyMeta: null,
      groupOptions: [],
    };
  }
};

const permissionSummary = computed(() => {
  if (localAccessLevel.value === accessLevels.EVERYONE) return "Everyone";
  if (localAccessLevel.value === accessLevels.PRIVATE) return "Private";
  return selectedCountNumbers.value || "Team";
});

const selectedCountNumbers = computed(() => {
  const { users = [], teams = [] } = selectionByTab.value;

  const counts = [
    { count: users.length, label: "User" },
    { count: teams.length, label: "Team" },
  ];

  const parts = counts
    .filter(({ count }) => count > 0)
    .map(({ count, label }) => `${count} ${label}${count > 1 ? "s" : ""}`);

  return parts.length ? parts.join(", ") : "";
});

const currentUserAsNonRemovable = computed(() => {
  const currentUserId = localStorage.getItem("userId");
  return currentUserId ? [parseInt(currentUserId)] : [];
});

const shouldEnableAutoSelect = computed(() => {
  const isEditMode = currentMode.value === "edit";

  if (!isEditMode) return true;

  return localAccessLevel.value === accessLevels.TEAM;
});

const groupByAttributeName = computed(() => {
  if (!localGroupBy.value) return "None";

  const isTasks = props.entitySlug === entityTypes.TASKS;
  const isDeal = props.entitySlug === entityTypes.DEALS;

  if (groupByConfigData.value?.selectedPropertyMeta?.name) {
    return groupByConfigData.value.selectedPropertyMeta.name;
  }

  if (
    isDeal &&
    (localGroupBy.value === "pipeline_id" ||
      localGroupBy.value === "pipeline_stage_id")
  ) {
    const savedPipelineId = props.currentView?.group_by_config?.pipeline_id;
    if (savedPipelineId) {
      const pipelines = store.getters["pipelines/allPipelines"] || [];
      const pipeline = pipelines.find((p) => p.id === savedPipelineId);
      if (pipeline) {
        return pipeline.name;
      }
    }
    return "Pipeline";
  }

  const schemaFields =
    store.getters["schema/getSelectFieldsBySlug"](
      props.entitySlug,
      isTasks ? props.projectId : null,
      {},
      props.service,
    ) || [];

  let selectFields = schemaFields;
  if (isDeal) {
    const pipelines = store.getters["pipelines/allPipelines"] || [];
    const pipelineFields = pipelines.map((pipeline) => ({
      id: `pipeline_${pipeline.id}`,
      slug: "pipeline_id",
      name: pipeline.name,
      type: "pipeline",
      isPipelineGroupBy: true,
      pipelineId: pipeline.id,
    }));
    selectFields = [...pipelineFields, ...schemaFields];
  }

  const field = selectFields.find(
    (f) => f.id === localGroupBy.value || f.slug === localGroupBy.value,
  );
  return field ? field.name : localGroupBy.value;
});

const isEditable = computed(
  () => currentMode.value !== "edit" || !!props.currentView?.can_edit,
);

const navigationOptions = computed(() => {
  if (props.exportOnly) return [];

  return [
    {
      key: "viewType",
      label: "Layout",
      value: localViewType.value === "board" ? "Board" : "Table",
      icon: imageNames.DASHBOARD_GREY,
      visible: true,
      disabled: !isEditable.value,
      onClick: openLayout,
    },
    {
      key: "groupBy",
      label: "Group By",
      value: groupByAttributeName.value,
      icon: imageNames.CARDS_GREY,
      visible: localViewType.value === "board",
      disabled: !isEditable.value,
      onClick: openGroupBy,
    },
    {
      key: "permissions",
      label: "Permissions",
      value: permissionSummary.value,
      icon: imageNames.LOCK_GREY,
      visible: !props.hidePermissionsOption,
      disabled: !isEditable.value,
      onClick: openPermissions,
    },
    {
      key: "propertyVisibility",
      label: "Property visibility",
      value: "",
      icon: imageNames.PROPERTY_VISIBILITY_GREY,
      visible: currentMode.value === "edit",
      disabled: !isEditable.value,
      onClick: openManageColumns,
    },
  ];
});

const handleSelectionChange = (data) => {
  if (data.byTab) {
    selectionByTab.value = data.byTab;
  }
};

const initializeCreateMode = async (viewType) => {
  currentMode.value = "create";
  localViewName.value = "Untitled View";
  localViewType.value = viewType || "table";
  localGroupBy.value = null;
  localAccessLevel.value = accessLevels.EVERYONE;
  selectedMembersLocal.value = [];
  selectionByTab.value = { users: [], teams: [] };

  groupByConfigData.value = {
    selectedPropertyMeta: null,
    groupOptions: [],
  };

  if (viewType === "board") {
    const isTasks = props.entitySlug === entityTypes.TASKS;
    const isDeal = props.entitySlug === entityTypes.DEALS;

    if (isDeal) {
      const isPipelinesLoaded = store.getters["pipelines/isPipelinesLoaded"];
      if (!isPipelinesLoaded) {
        await store.dispatch("pipelines/fetchPipelines");
      }
    }

    const selectFields =
      store.getters["schema/getSelectFieldsBySlug"](
        props.entitySlug,
        isTasks ? props.projectId : null,
        { excludeGroupBy: true },
        props.service,
      ) || [];

    let fieldsToCheck = selectFields;
    if (isDeal) {
      const pipelines = store.getters["pipelines/allPipelines"] || [];
      const pipelineFields = pipelines.map((pipeline) => ({
        id: `pipeline_${pipeline.id}`,
        slug: "pipeline_id",
        name: pipeline.name,
        type: "pipeline",
        isPipelineGroupBy: true,
        pipelineId: pipeline.id,
      }));
      fieldsToCheck = [...pipelineFields, ...selectFields];
    }

    if (fieldsToCheck.length > 0) {
      const firstSelectField = fieldsToCheck[0];
      localGroupBy.value = firstSelectField.slug;

      try {
        await loadGroupOptionsForAutoSelect(firstSelectField);
      } catch (error) {
        console.error("Error loading group options for view creation:", error);
      }
    }
  }

  if (moreOptionsDropdownRef.value) {
    moreOptionsDropdownRef.value.isOpen = true;
    nextTick(() => {
      const inputElement = document.getElementById("view_name_input");
      if (inputElement) {
        inputElement.focus();
      }
    });
  }
};

const initializeEditMode = (shouldOpen = false) => {
  currentMode.value = "edit";

  if (props.currentView) {
    localViewName.value = props.currentView.name || "";
    localViewType.value = props.currentView.view_type || "table";

    if (props.currentView.group_by_config) {
      const attributeSlug = props.currentView.group_by_config.attribute_slug;

      if (
        attributeSlug === "pipeline_stage_id" &&
        props.currentView.group_by_config.pipeline_id
      ) {
        const pipelineId = props.currentView.group_by_config.pipeline_id;
        localGroupBy.value = `pipeline_${pipelineId}`;

        const pipelines = store.getters["pipelines/allPipelines"] || [];
        const pipeline = pipelines.find((p) => p.id === pipelineId);
        if (pipeline) {
          groupByConfigData.value = {
            selectedPropertyMeta: {
              id: `pipeline_${pipelineId}`,
              slug: "pipeline_id",
              name: pipeline.name,
              type: "pipeline",
              isPipelineGroupBy: true,
              pipelineId: pipelineId,
            },
            groupOptions: props.currentView.group_by_config.options || [],
          };
        }
      } else {
        localGroupBy.value = attributeSlug || null;
        groupByConfigData.value = {
          selectedPropertyMeta: null,
          groupOptions: [],
        };
      }
    } else {
      localGroupBy.value = props.currentView.group_by || null;
      groupByConfigData.value = {
        selectedPropertyMeta: null,
        groupOptions: [],
      };
    }

    if (props.currentView.sharing_scope === sharingScopes.WORKSPACE) {
      localAccessLevel.value = accessLevels.EVERYONE;
    } else if (
      props.currentView.sharing_scope === sharingScopes.TEAM_AND_USERS
    ) {
      localAccessLevel.value = accessLevels.TEAM;
    } else {
      localAccessLevel.value = accessLevels.PRIVATE;
    }
  }

  let userIds = [];
  let teamIds = [];

  if (props.currentView) {
    userIds = props.currentView.user_ids || [];
    teamIds = props.currentView.team_ids || [];

    selectionByTab.value = {
      users: [...userIds],
      teams: [...teamIds],
    };

    selectedMembersLocal.value = [...userIds, ...teamIds];
  }

  initialState.value = {
    name: localViewName.value,
    viewType: localViewType.value,
    groupBy: localGroupBy.value,
    accessLevel: localAccessLevel.value,
    sharingScope: props.currentView?.sharing_scope || sharingScopes.PERSONAL,
    teamIds: localAccessLevel.value === accessLevels.TEAM ? [...teamIds] : [],
    userIds: localAccessLevel.value === accessLevels.TEAM ? [...userIds] : [],
  };

  if (shouldOpen) {
    if (moreOptionsDropdownRef.value) {
      moreOptionsDropdownRef.value.isOpen = true;
      nextTick(() => {
        const inputElement = document.getElementById("view_name_input");
        if (inputElement) {
          inputElement.focus();
        }
      });
    }
  }
};

const buildSaveData = () => {
  let sharingScope = sharingScopes.PERSONAL;
  let teamIds = [];
  let userIds = [];

  if (localAccessLevel.value === accessLevels.EVERYONE) {
    sharingScope = sharingScopes.WORKSPACE;
  } else if (localAccessLevel.value === accessLevels.TEAM) {
    sharingScope = sharingScopes.TEAM_AND_USERS;
    userIds = selectionByTab.value.users || [];
    teamIds = selectionByTab.value.teams || [];

    const currentUserId = localStorage.getItem("userId");
    if (currentUserId && !userIds.includes(parseInt(currentUserId))) {
      userIds.push(parseInt(currentUserId));
    }
  }

  let groupByConfig = null;

  if (localViewType.value === "board" && localGroupBy.value) {
    if (
      groupByPanelRef.value?.selectedPropertyMeta &&
      groupByPanelRef.value?.groupOptions
    ) {
      groupByConfigData.value = {
        selectedPropertyMeta: groupByPanelRef.value.selectedPropertyMeta,
        groupOptions: groupByPanelRef.value.groupOptions,
      };
    }

    const { selectedPropertyMeta, groupOptions } = groupByConfigData.value;

    if (selectedPropertyMeta && groupOptions?.length > 0) {
      const attributeSlug = selectedPropertyMeta.isPipelineGroupBy
        ? "pipeline_stage_id"
        : selectedPropertyMeta.slug;

      groupByConfig = {
        attribute_id: selectedPropertyMeta.id,
        attribute_slug: attributeSlug,
        options: groupOptions.map((opt, index) => ({
          id: opt.apiId || opt.id,
          value: opt.value,
          position: index,
          is_visible: (opt.visible ?? opt.is_visible) !== false,
        })),
      };

      if (
        selectedPropertyMeta.isPipelineGroupBy &&
        selectedPropertyMeta.pipelineId
      ) {
        groupByConfig.pipeline_id = selectedPropertyMeta.pipelineId;
      }
    } else if (
      currentMode.value === "edit" &&
      props.currentView?.group_by_config?.attribute_slug === localGroupBy.value
    ) {
      groupByConfig = props.currentView.group_by_config;
    }
  }

  return {
    name: localViewName.value.trim(),
    sharing_scope: sharingScope,
    team_ids: teamIds,
    user_ids: userIds,
    view_type: localViewType.value,
    group_by_config: groupByConfig,
    access_level: localAccessLevel.value,
  };
};

const handleSaveView = async () => {
  if (!localViewName.value || !localViewName.value.trim()) {
    return;
  }

  if (isSavingView.value) {
    return;
  }

  if (localViewType.value === "board" && !localGroupBy.value) {
    message.error("Board view requires a Group By selection");
    return;
  }

  const saveData = buildSaveData();
  const isTasks = props.entitySlug === entityTypes.TASKS;

  try {
    isSavingView.value = true;
    
    if (currentMode.value === "edit") {
      if (!props.currentView?.id) {
        message.error("No view selected to update");
        return;
      }

      const payload = {
        view: {
          name: saveData.name,
          sharing_scope: saveData.sharing_scope,
          view_type: saveData.view_type,
          group_by_config: saveData.group_by_config,
        },
        team_ids: saveData.team_ids || [],
        user_ids: saveData.user_ids || [],
        ...(props.service && { service: props.service }),
      };

      let response;
      if (isTasks) {
        response = await ViewsServiceV2.updateView(
          props.currentView.id,
          payload,
        );
      } else {
        response = await DashboardService.updateView(
          props.currentView.id,
          payload,
        );
      }

      if (response?.success) {
        message.success("View updated successfully");
        if (response?.view) {
          emit("view-updated", { view: response.view });
        } else {
          emit("view-updated");
        }
      } else {
        message.error(response?.errors || "Failed to update view");
      }
    } else {
      const entityType = isTasks
        ? viewEntityMapping[entityTypes.PROJECTS]
        : props.slug;

      const payload = {
        name: saveData.name,
        entity_type: entityType,
        filters: {},
        sort: [],
        sharing_scope: saveData.sharing_scope,
        team_ids: saveData.team_ids || [],
        user_ids: saveData.user_ids || [],
        view_type: saveData.view_type,
        group_by_config: saveData.group_by_config,
        ...(props.service && { service: props.service }),

      };

      if (props.scopeEntityId) {
        payload.entity_id = props.scopeEntityId;
      }

      let response;
      if (isTasks) {
        if (!props.projectId) {
          message.error("Project ID is required for task views");
          return;
        }
        const v2Payload = {
          ...payload,
          entity_id: props.projectId,
        };
        response = await ViewsServiceV2.createNewView(v2Payload);
      } else {
        response = await DashboardService.createNewView(payload);
      }

      if (response?.success) {
        message.success("View created successfully");
        emit("view-created");
      } else {
        message.error(response?.errors || "Failed to create view");
      }
    }

    moreOptionsDropdownRef.value?.closeDropdown?.();
  } catch (error) {
    console.error("Error saving view:", error);
    message.error(
      error.response?.data?.errors ||
        error.response?.data?.error ||
        "Failed to save view",
    );
  } finally {
    isSavingView.value = false;
  }
};

const hasChanges = () => {
  const currentSharingScope =
    localAccessLevel.value === accessLevels.EVERYONE
      ? sharingScopes.WORKSPACE
      : localAccessLevel.value === accessLevels.TEAM
        ? sharingScopes.TEAM_AND_USERS
        : sharingScopes.PERSONAL;

  const currentTeamIds =
    localAccessLevel.value === accessLevels.TEAM
      ? selectionByTab.value.teams || []
      : [];

  const currentUserIds =
    localAccessLevel.value === accessLevels.TEAM
      ? selectionByTab.value.users || []
      : [];

  const arraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    const sorted1 = [...arr1].map((id) => Number(id)).sort((a, b) => a - b);
    const sorted2 = [...arr2].map((id) => Number(id)).sort((a, b) => a - b);
    return sorted1.every((val, idx) => val === sorted2[idx]);
  };

  const nameChanged = localViewName.value?.trim() !== initialState.value.name;
  const viewTypeChanged = localViewType.value !== initialState.value.viewType;
  const groupByChanged = localGroupBy.value !== initialState.value.groupBy;
  const sharingChanged =
    currentSharingScope !== initialState.value.sharingScope;
  const teamsChanged = !arraysEqual(currentTeamIds, initialState.value.teamIds);
  const usersChanged = !arraysEqual(currentUserIds, initialState.value.userIds);

  return (
    nameChanged ||
    viewTypeChanged ||
    groupByChanged ||
    sharingChanged ||
    teamsChanged ||
    usersChanged
  );
};

const handleAutoSave = async () => {
  if (!localViewName.value || !localViewName.value.trim()) {
    return;
  }

  if (localViewType.value === "board" && !localGroupBy.value) {
    message.error("Board view requires a Group By selection");
    return;
  }

  if (!hasChanges()) {
    return;
  }

  if (!props.currentView?.id) {
    return;
  }

  const saveData = buildSaveData();
  const isTasks = props.entitySlug === entityTypes.TASKS;

  try {
    const payload = {
      view: {
        name: saveData.name,
        sharing_scope: saveData.sharing_scope,
        view_type: saveData.view_type,
        group_by_config: saveData.group_by_config,
      },
      team_ids: saveData.team_ids || [],
      user_ids: saveData.user_ids || [],
    };

    let response;
    if (isTasks) {
      response = await ViewsServiceV2.updateView(props.currentView.id, payload);
    } else {
      response = await DashboardService.updateView(
        props.currentView.id,
        payload,
      );
    }

    if (response?.success) {
      emit("view-updated", { view: response?.view });
    }
  } catch (error) {
    console.error("Error auto-saving view:", error);
  }
};

watch(
  () => props.currentView?.id,
  (newId, oldId) => {
    if (newId !== oldId && isMoreOptionsOpen.value) {
      initializeEditMode();
    }
  },
);

watch(
  () => moreOptionsDropdownRef.value?.isOpen,
  (newValue, oldValue) => {
    if (newValue !== undefined) {
      isMoreOptionsOpen.value = newValue;
      if (newValue) {
        if (!currentMode.value) {
          initializeEditMode();
        }
        nextTick(() => {
          const inputElement = document.getElementById("view_name_input");
          if (inputElement) {
            inputElement.focus();
          }
        });
      } else {
        showManageColumnsContent.value = false;
        showPermissionsContent.value = false;
        showLayoutContent.value = false;
        showGroupByContent.value = false;
        currentMode.value = null;
      }
    }
  },
);

const handleOpenViewCreateMode = (viewType) => {
  initializeCreateMode(viewType);
};

const handleOpenViewEditMode = () => {
  initializeEditMode(true);
};

onMounted(() => {
  eventBus.on("open-view-create-mode", handleOpenViewCreateMode);
  eventBus.on("open-view-edit-mode", handleOpenViewEditMode);
});

onBeforeUnmount(() => {
  eventBus.off("open-view-create-mode", handleOpenViewCreateMode);
  eventBus.off("open-view-edit-mode", handleOpenViewEditMode);
});

defineExpose({
  closeDropdown: () => moreOptionsDropdownRef.value?.closeDropdown(),
  openDropdown: () => {
    if (moreOptionsDropdownRef.value) {
      moreOptionsDropdownRef.value.isOpen = true;
    }
  },
  initializeEditMode,
});
</script>
